// Base API configuration and utilities
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:9011/api';

// Generic API client
class ApiClient {
  private baseURL: string;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;

    const config: RequestInit = {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        // Try to get validation error details from response body
        let errorDetails = '';
        try {
          const errorBody = await response.json();
          if (errorBody.details) {
            // Joi validation error format
            errorDetails = errorBody.details
              .map((d: { path: string; msg: string }) => `${d.path}: ${d.msg}`)
              .join('; ');
          } else if (errorBody.error) {
            errorDetails = errorBody.error;
          }
          console.error('API validation error:', errorBody);
        } catch {
          // Response body wasn't JSON
        }
        throw new Error(
          `API Error: ${response.status} ${response.statusText}${errorDetails ? ` - ${errorDetails}` : ''}`,
        );
      }

      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  async get<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET', ...options });
  }

  async post<T>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });
  }

  async put<T>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });
  }

  async delete<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE', ...options });
  }

  async patch<T>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });
  }
}

// Export singleton instance
export const apiClient = new ApiClient();

// Export the class for custom instances
export { ApiClient };
